import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import Logo from '../../components/Logo.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import * as api from '../../db/api.js'
import { translateOrgError, validateOrgName } from '../../lib/onboarding.js'

// Onboarding — ponto de convergência pós-cadastro. Dois fluxos:
//
//   Fluxo 1 (founder): convite de ACESSO (access_invite) → 2 passos:
//     1. nome da organização (mín. 3 caracteres, mesmo limite do RPC) + tipo
//        (empresa/agência) → RPC create_initial_organization. Desde a
//        migration public/002 (#239) o RPC cria, na mesma transação, a org,
//        a membership owner e o cliente próprio (is_self, com o nome da org,
//        sem domínio). O front ainda chama POST /api/clients {isSelf: true},
//        que é idempotente: reutiliza o cliente do RPC (ou o cria, se o banco
//        ainda não tiver a migration). Se essa chamada falhar, o usuário vê um
//        aviso com "Tentar novamente" no passo 2 — não fica travado.
//     2. integrações (Google Ads / Meta Ads) — OPCIONAIS. Usuário pode pular
//        e conectar depois em Configurações → Conexões; nada aqui bloqueia
//        a navegação para o dashboard.
//     A org e o cliente existem ANTES de qualquer OAuth: se o usuário pular
//     ou abandonar a conexão, ainda entra no dashboard com dado real.
//
//   Fluxo 2 (join): convite de ORGANIZAÇÃO (código de uso único, sem vínculo a
//     e-mail) → não cria org nem cliente. POST /api/me/join-organization cria a
//     membership com o perfil do convite e materializa user_client_access SÓ
//     para os clients listados no convite (#175); perfil admin tem acesso
//     implícito a todos os clients da org. Depois rehidrata o acesso e segue
//     pro dashboard. Quem entrou por convite NÃO cria organização própria
//     (o RPC recusa) — por isso o wizard de criação nunca aparece nesse fluxo.
export default function Onboarding() {
  const navigate = useNavigate()
  const app = useApp()
  const { createInitialOrganization, user } = useAuth()

  // Fluxo 2: executa o join a partir do convite no metadata. Roda uma única vez
  // enquanto o acesso ainda é 'none'. Sem org_invite_code, é founder → wizard.
  const orgInviteCode = user?.user_metadata?.org_invite_code || null
  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState('')
  const [joinRetry, setJoinRetry] = useState(0)
  const joinAttempted = useRef(false)

  // ---- Fluxo 1 (founder) — estado do wizard de 2 passos ----
  const [step, setStep] = useState(1)

  const [orgName, setOrgName] = useState('')
  const [orgNameTouched, setOrgNameTouched] = useState(false)
  const [orgType, setOrgType] = useState('company')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Aviso quando o cliente próprio (is_self) não pôde ser confirmado.
  const [selfClientWarning, setSelfClientWarning] = useState('')
  const [retryingSelfClient, setRetryingSelfClient] = useState(false)

  const [connecting, setConnecting] = useState(null) // 'google' | 'meta' | null

  useEffect(() => {
    if (joinAttempted.current) return
    if (!orgInviteCode) return
    if (app?.accessMode && app.accessMode !== 'none') return
    joinAttempted.current = true

    let active = true
    ;(async () => {
      try {
        setJoinError('')
        setJoining(true)
        await api.joinOrganization(orgInviteCode)
        if (!active) return

        // Rehidrata o acesso furando o cache; só navega quando deixou de ser 'none'.
        let resolvedMode = null
        try {
          resolvedMode = await app?.refreshAccess?.()
        } catch (err) {
          console.warn('[Onboarding] refreshAccess (join) falhou:', err?.message)
        }
        if (!active) return
        if (resolvedMode === 'none') {
          setJoinError('Acesso concedido, mas ainda não propagou. Aguarde alguns segundos e tente novamente.')
          setJoining(false)
          return
        }
        navigate('/google-ads', { replace: true })
      } catch (err) {
        if (!active) return
        console.error('[Onboarding] join falhou:', err)
        setJoinError(err?.message || 'Não foi possível entrar na organização do convite.')
        setJoining(false)
      }
    })()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgInviteCode, joinRetry])

  async function retryJoin() {
    setJoinError('')
    setJoining(true)
    // O convite pode já ter sido consumido (acesso só não tinha propagado):
    // confere o acesso antes de tentar o join de novo.
    let mode = null
    try {
      mode = await app?.refreshAccess?.()
    } catch (err) {
      console.warn('[Onboarding] refreshAccess (retry join) falhou:', err?.message)
    }
    if (mode && mode !== 'none') {
      navigate('/google-ads', { replace: true })
      return
    }
    joinAttempted.current = false
    setJoinRetry((n) => n + 1)
  }

  // Conditional returns só DEPOIS de todos os hooks (regras dos hooks): no join,
  // accessMode transita 'none' → 'client_only'; retornar antes mudaria a
  // contagem de hooks entre renders e quebraria o React.
  // client_only (join já materializado) — manda direto pro dashboard.
  if (app?.accessMode === 'client_only') {
    return <Navigate to="/google-ads" replace />
  }

  // Fluxo 2: quem tem convite de organização nunca vê o wizard de criação de
  // org (o RPC recusaria). Com acesso já resolvido, segue pro dashboard;
  // senão mostra o progresso do join ou o erro com "Tentar novamente".
  if (orgInviteCode) {
    if (app?.accessMode && app.accessMode !== 'none' && !joining) {
      return <Navigate to="/google-ads" replace />
    }
    return (
      <div className="ob-shell">
        <div className="ob-topbar">
          <div className="ob-logo"><Logo height={16} /></div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/login')}>
            Sair
          </button>
        </div>
        <div className="ob-body">
          <div className="ob-card">
            <div className="ob-badge">Entrando na organização</div>
            <h1 className="ob-title">
              {joinError ? 'Não foi possível entrar na organização' : 'Conectando você aos clientes…'}
            </h1>
            <p className="ob-desc">
              {joinError
                ? 'Você se cadastrou com um convite de organização, então o acesso vem da organização que convidou você — não é possível criar uma organização própria. Confira o erro abaixo ou peça um novo convite ao responsável.'
                : 'Estamos liberando o acesso aos clientes da organização do seu convite.'}
            </p>
            {joinError ? (
              <>
                <div className="auth-error" style={{ marginTop: 16 }}>{joinError}</div>
                <button
                  className="btn btn-black btn-full"
                  type="button"
                  onClick={retryJoin}
                  disabled={joining}
                  style={{ marginTop: 16 }}
                >
                  Tentar novamente
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  const orgNameError = validateOrgName(orgName)
  const orgValid = !orgNameError

  // Issue #102: o RPC create_initial_organization commita a organização e a
  // membership owner antes de retornar, mas a leitura seguinte (refreshAccess
  // -> GET /api/me/access?refresh=true) pode, em casos raros, ainda enxergar
  // access_mode='none' (latência de propagação entre a escrita via RPC do
  // Supabase e a query subsequente no FastAPI). Sem retry, isso travava o
  // usuário no step 1 com a org já persistida no banco. Tenta algumas vezes
  // com backoff antes de expor o erro definitivo.
  async function confirmAccessWithRetry(attempts = 4, baseDelayMs = 700) {
    for (let i = 0; i < attempts; i++) {
      let mode = null
      try {
        mode = await app?.refreshAccess?.()
      } catch (err) {
        console.warn('[Onboarding] refreshAccess falhou:', err?.message)
      }
      if (mode !== 'none') return mode
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (i + 1)))
      }
    }
    return 'none'
  }

  // Garante o cliente próprio (is_self). Idempotente no backend: reutiliza o
  // cliente criado pelo RPC (migration public/002) ou o cria. Tenta 2 vezes.
  async function ensureSelfClient(name, attempts = 2) {
    let lastErr = null
    for (let i = 0; i < attempts; i++) {
      try {
        await api.createClient({ name, isSelf: true })
        try {
          await app?.refreshAccess?.()
        } catch (err) {
          console.warn('[Onboarding] refreshAccess (is_self) falhou:', err?.message)
        }
        return true
      } catch (err) {
        lastErr = err
        if (i < attempts - 1) await new Promise((resolve) => setTimeout(resolve, 800))
      }
    }
    console.error('[Onboarding] createClient (is_self) falhou:', lastErr)
    return false
  }

  const SELF_CLIENT_WARNING =
    'Sua organização foi criada, mas não conseguimos confirmar o cliente principal dela. ' +
    'Tente novamente abaixo ou crie o cliente depois em Configurações → Clientes.'

  async function retrySelfClient() {
    setRetryingSelfClient(true)
    const ok = await ensureSelfClient(orgName.trim(), 1)
    setRetryingSelfClient(false)
    if (ok) {
      setSelfClientWarning('')
      app?.toast?.('Cliente principal confirmado.', 'success')
    } else {
      setSelfClientWarning(SELF_CLIENT_WARNING)
    }
  }

  async function handleOrgSubmit(event) {
    event.preventDefault()
    setOrgNameTouched(true)
    if (submitting || !orgValid) return
    setError('')
    setSubmitting(true)

    // 1. Organização + membership owner + cliente is_self (RPC Supabase).
    let orgErr
    try {
      const result = await createInitialOrganization(orgName.trim(), orgType)
      orgErr = result?.error || null
    } catch (err) {
      orgErr = err
    }
    if (orgErr) {
      console.error('[Onboarding] create_initial_organization falhou:', orgErr)
      setError(translateOrgError(orgErr))
      setSubmitting(false)
      return
    }

    // 2. Confirma o acesso furando o cache do backend (org recém-criada via RPC),
    //    com retry/backoff — ver confirmAccessWithRetry.
    const mode = await confirmAccessWithRetry()
    if (mode === 'none') {
      setError('Organização criada, mas o acesso ainda não foi liberado. Aguarde alguns segundos e tente novamente.')
      setSubmitting(false)
      return
    }

    // 3. Cliente "self" com o nome da organização (empresa e agência seguem o
    //    mesmo caminho). Domínio não é obrigatório e o nome pode ser alterado
    //    depois em Configurações → Clientes. Com a migration public/002 o RPC
    //    já criou o cliente e esta chamada só o confirma (idempotente). Se
    //    falhar, a org já existe e o acesso foi concedido: o usuário segue,
    //    mas vê o aviso com "Tentar novamente" no passo 2.
    const selfOk = await ensureSelfClient(orgName.trim())
    setSelfClientWarning(selfOk ? '' : SELF_CLIENT_WARNING)
    setSubmitting(false)
    setStep(2)
  }

  async function handleConnect(provider) {
    setConnecting(provider)
    try {
      const { authorize_url: url } =
        provider === 'google' ? await api.startGoogleAdsOAuth() : await api.startMetaAdsOAuth()
      if (!url) throw new Error('URL de autorização vazia.')
      window.location.assign(url)
    } catch (err) {
      setConnecting(null)
      app?.toast?.(err.message || 'Falha ao iniciar a conexão.', 'error')
    }
  }

  function finish() {
    navigate('/google-ads', { replace: true })
  }

  const STEPS = [
    { n: 1, label: 'Organização' },
    { n: 2, label: 'Integrações' },
  ]

  return (
    <div className="ob-shell">
      <div className="ob-topbar">
        <div className="ob-logo"><Logo height={16} /></div>
        <div className="ob-steps">
          {STEPS.map((s, i) => (
            <div key={s.n} className="ob-step-wrapper">
              <div className="ob-step">
                <span
                  className={`ob-step-num ${
                    step === s.n ? 'active' : step > s.n ? 'done' : 'future'
                  }`}
                >
                  {step > s.n ? '✓' : s.n}
                </span>
                <span
                  className={`ob-step-label ${
                    step === s.n ? 'active' : step > s.n ? '' : 'future'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`ob-connector ${step > s.n ? 'done' : ''}`} />
              )}
            </div>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/login')}>
          Sair
        </button>
      </div>

      <div className="ob-body">
        <div className="ob-card">
          {step === 1 && (
            <form onSubmit={handleOrgSubmit}>
              <div className="ob-badge">Configuração inicial</div>
              <h1 className="ob-title">Vamos criar seu workspace</h1>
              <p className="ob-desc">
                Crie sua organização. As integrações (Google Ads, Meta Ads) são
                opcionais — você pode conectar agora ou depois, quando quiser.
              </p>

              {error ? (
                <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>
              ) : null}

              <div className="field">
                <label className="field-label">Nome da organização</label>
                <input
                  className="input"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  onBlur={() => setOrgNameTouched(true)}
                  placeholder="Ex: Agência Exemplo"
                  autoFocus
                  disabled={submitting}
                  aria-invalid={orgNameTouched && !!orgNameError}
                />
                {orgNameTouched && orgNameError ? (
                  <div className="auth-error" style={{ marginTop: 8 }}>{orgNameError}</div>
                ) : null}
              </div>

              <div className="field">
                <label className="field-label">Tipo de organização</label>
                <div className="ob-options">
                  {[
                    { v: 'company', t: 'Empresa', d: 'Gerencio o marketing da minha própria empresa.' },
                    { v: 'agency', t: 'Agência', d: 'Opero marketing e performance de clientes.' },
                  ].map((o) => (
                    <button
                      type="button"
                      key={o.v}
                      className={`ob-option ${orgType === o.v ? 'sel' : ''}`}
                      onClick={() => setOrgType(o.v)}
                      disabled={submitting}
                    >
                      <div className="ob-option-title">{o.t}</div>
                      <div className="ob-option-desc">{o.d}</div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                className="btn btn-black btn-full"
                type="submit"
                disabled={!orgValid || submitting}
                style={{ marginTop: 8 }}
              >
                {submitting ? 'Criando…' : 'Continuar'}
              </button>
            </form>
          )}

          {step === 2 && (
            <>
              <div className="ob-badge">Passo final</div>
              <h1 className="ob-title">Conecte suas integrações</h1>
              <p className="ob-desc">
                Totalmente opcional. Você já pode explorar a plataforma sem
                conectar nada agora — conecte quando quiser em Configurações →
                Conexões.
              </p>

              {selfClientWarning ? (
                <div className="auth-error" style={{ marginBottom: 16 }}>
                  {selfClientWarning}
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={retrySelfClient}
                      disabled={retryingSelfClient}
                    >
                      {retryingSelfClient ? 'Tentando…' : 'Tentar novamente'}
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="ob-options">
                <button
                  type="button"
                  className="ob-option"
                  onClick={() => handleConnect('google')}
                  disabled={!!connecting}
                >
                  <div className="ob-option-title">Google Ads</div>
                  <div className="ob-option-desc">Importar campanhas e métricas.</div>
                </button>
                <button
                  type="button"
                  className="ob-option"
                  onClick={() => handleConnect('meta')}
                  disabled={!!connecting}
                >
                  <div className="ob-option-title">Meta Ads</div>
                  <div className="ob-option-desc">Importar campanhas do Facebook/Instagram.</div>
                </button>
              </div>

              <button
                className="btn btn-black btn-full"
                type="button"
                onClick={finish}
                disabled={!!connecting}
                style={{ marginTop: 8 }}
              >
                {connecting ? 'Redirecionando…' : 'Pular por enquanto'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
